import React, { useState, useEffect, useRef } from "react";
import { fetchPostApi } from "../../../helpers/fetch";
import { VERIFY_EMAIL_BANNER_EMAIL_KEY } from "../../constants/signupFlow";
import "./Auth.scss";

// Module-level guard: suppresses React StrictMode's double-mount (same page load)
// while still allowing a fresh page load (e.g. a second expired link) to auto-fire.
let autoFired = false;

function maskEmail(email) {
  const [local, domain] = email.split("@");
  if (!domain) return email;
  const visible = local.slice(0, 2);
  return `${visible}${"*".repeat(Math.max(local.length - 2, 1))}@${domain}`;
}

const ExpiredLinkResend = ({ initialEmail }) => {
  const [status, setStatus] = useState(initialEmail ? "sending" : "idle");
  const [email, setEmail] = useState(initialEmail || "");
  const [message, setMessage] = useState("");
  const [showForm, setShowForm] = useState(!initialEmail);
  const hasFired = useRef(false);

  const sendResend = async (targetEmail) => {
    setStatus("sending");
    setMessage("");
    try {
      const data = await fetchPostApi("/api/auth/resend-confirmation", {
        email: targetEmail,
      });
      setStatus("sent");
      setMessage(
        data.message ||
          "We've sent you a new confirmation link. Check your inbox.",
      );
      try {
        localStorage.setItem(VERIFY_EMAIL_BANNER_EMAIL_KEY, targetEmail);
      } catch {
        /* ignore */
      }
    } catch (err) {
      const resp = err?.response;
      if (err?.status === 429 || resp?.retryAfter) {
        setStatus("cooldown");
        setMessage("Please wait a minute before requesting another email.");
        setTimeout(() => setStatus("idle"), 60_000);
      } else {
        setStatus("error");
        setMessage(
          resp?.message || "Something went wrong. Please try again.",
        );
      }
    }
  };

  useEffect(() => {
    if (!initialEmail || hasFired.current || autoFired) return;
    autoFired = true;
    hasFired.current = true;
    sendResend(initialEmail);
  }, [initialEmail]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    sendResend(trimmed);
  };

  return (
    <div className="auth-container d-flex f-column aic jcc gap-16 p-16">
      <h2 className="fs-lg fw-bold ta-center">
        {status === "sent"
          ? "New link sent!"
          : "This confirmation link has expired"}
      </h2>

      {status === "sending" && (
        <p className="fs-regular ta-center">Sending you a new link...</p>
      )}

      {status === "sent" && (
        <div className="d-flex f-column aic gap-8">
          <p className="fs-regular ta-center">{message}</p>
          {initialEmail && !showForm && (
            <p className="fs-xs ta-center">
              Sent to <strong>{maskEmail(initialEmail)}</strong>.{" "}
              <button
                type="button"
                className="link-btn fs-xs"
                onClick={() => {
                  setShowForm(true);
                  setStatus("idle");
                }}
              >
                Use a different email
              </button>
            </p>
          )}
        </div>
      )}

      {status === "cooldown" && (
        <p className="error-message fs-regular ta-center">{message}</p>
      )}

      {status === "error" && (
        <div className="d-flex f-column aic gap-8">
          <p className="error-message fs-regular ta-center">{message}</p>
          <button
            type="button"
            className="btn red-btn btn-full fs-regular"
            onClick={() => sendResend(email.trim())}
          >
            Try again
          </button>
        </div>
      )}

      {(status === "idle" || showForm) && status !== "sending" && status !== "sent" && (
        <form
          onSubmit={handleSubmit}
          className="d-flex f-column aic gap-8 w-100"
          style={{ maxWidth: 360 }}
        >
          <p className="fs-regular ta-center">
            Enter your email and we'll send a fresh confirmation link.
          </p>
          <input
            type="email"
            className="input-main w-100"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
          <button
            type="submit"
            className="btn red-btn btn-full fs-regular"
            disabled={!email.trim()}
          >
            Send me a new link
          </button>
        </form>
      )}
    </div>
  );
};

export default ExpiredLinkResend;
